import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";

import { notificationStore, NotificationType } from "../../store/notification";
import {
    getAdvertiserId,
    getMarketplaceId,
} from "../../utils/permission.utils";
import type { Item } from "../common";

export interface FilterButtonActionButton {
    label: string;
    featureFlag?: string;
    onClick: () => void;
    external?: boolean;
}

export enum UnifiedFilterField {
    BRAND = "UNIFIED_BRANDNAME",
    BROWSE_NODE = "UNIFIED_BROWSENODENAME",
    OBJECTIVE = "UNIFIED_OBJECTIVES",
    TOPIC = "UNIFIED_TOPIC",
    LANGUAGE = "UNIFIED_LANGUAGE",
}

export type UnifiedFilterSelections = {
    [key in UnifiedFilterField]: Item[];
};
export type UnifiedFilterOptions = {
    [key in UnifiedFilterField]: Item[];
};

// creating a new enum because allPackagedSearch depends on the list above.
export enum CoreUnifiedFilterKeys {
    TYPE = "UNIFIED_TYPE",
}

export enum UnifiedFilterKeys {
    PACKAGE_NAME = "UNIFIED_PACKAGENAME",
    ENTITY_NAME = "UNIFIED_ENTITYNAME",
    TOPIC = "UNIFIED_TOPIC",
    ADVERTISER_GOAL = "UNIFIED_ADVERTISERGOAL",
    AD_ACTIVATION_CHANNEL = "UNIFIED_ADACTIVATIONCHANNEL",
    SEASONAL_EVENTS = "UNIFIED_SEASONALEVENTS",
    LANGUAGE = "UNIFIED_LANGUAGE",
    MARKETING_STRATEGY = "UNIFIED_MARKETINGSTRATEGY",
    SUB_VERTICAL = "UNIFIED_SUBVERTICAL",
    VERTICAL = "UNIFIED_VERTICAL",
    FILE_TYPE = "UNIFIED_FILETYPE",
    BRAND_NAME = "UNIFIED_BRANDNAME",
    MARKETPLACE = "UNIFIED_MARKETPLACE",
    OBJECTIVES = "UNIFIED_OBJECTIVES",
    PRIMARY_CONTENT_CREATOR_TEAM = "UNIFIED_PRIMARYCONTENTCREATORTEAM",
    BROWSE_NODE_NAME = "UNIFIED_BROWSENODENAME",
    AD_PRODUCTS_AND_FORMATS = "UNIFIED_ADPRODUCTSANDFORMATS",
    MEETING_TYPE = "MEETING_TYPE",
    MEETING_SUB_TYPE = "MEETING_SUB_TYPE",
    OPPORTUNITY_CATEGORY = "OPPORTUNITY_CATEGORY",
    OPPORTUNITY_STATUS = "OPPORTUNITY_STATUS",
}

export type AllUnifiedFilterKeys = CoreUnifiedFilterKeys | UnifiedFilterKeys;

/**
 * A map of UI friends names for each filter key
 */
export const UnifiedFilterReadableNames: Record<AllUnifiedFilterKeys, string> =
    {
        [CoreUnifiedFilterKeys.TYPE]: "Type",
        [UnifiedFilterKeys.PACKAGE_NAME]: "Package name",
        [UnifiedFilterKeys.ENTITY_NAME]: "Entity name",
        [UnifiedFilterKeys.TOPIC]: "Topic",
        [UnifiedFilterKeys.ADVERTISER_GOAL]: "Advertiser goal",
        [UnifiedFilterKeys.AD_ACTIVATION_CHANNEL]: "Ad activation channel",
        [UnifiedFilterKeys.SEASONAL_EVENTS]: "Seasonal events",
        [UnifiedFilterKeys.LANGUAGE]: "Language",
        [UnifiedFilterKeys.MARKETING_STRATEGY]: "Marketing strategy",
        [UnifiedFilterKeys.SUB_VERTICAL]: "Sub vertical",
        [UnifiedFilterKeys.VERTICAL]: "Vertical",
        [UnifiedFilterKeys.FILE_TYPE]: "File type",
        [UnifiedFilterKeys.BRAND_NAME]: "Brand name",
        [UnifiedFilterKeys.MARKETPLACE]: "Marketplace",
        [UnifiedFilterKeys.OBJECTIVES]: "Objectives",
        [UnifiedFilterKeys.PRIMARY_CONTENT_CREATOR_TEAM]:
            "Primary content creator team",
        [UnifiedFilterKeys.BROWSE_NODE_NAME]: "Browse node name",
        [UnifiedFilterKeys.AD_PRODUCTS_AND_FORMATS]: "Ad products and formats",
        [UnifiedFilterKeys.MEETING_TYPE]: "Meeting type",
        [UnifiedFilterKeys.MEETING_SUB_TYPE]: "Meeting sub type",
        [UnifiedFilterKeys.OPPORTUNITY_CATEGORY]: "Opportunity category",
        [UnifiedFilterKeys.OPPORTUNITY_STATUS]: "Opportunity status",
    };

export const unifiedFilterActionButtons: Partial<
    Record<AllUnifiedFilterKeys, FilterButtonActionButton[]>
> = {
    [UnifiedFilterKeys.BRAND_NAME]: [
        {
            label: "Manage Brand/Businessline Preferences",
            external: true,
            onClick: (): void => {
                const marketplaceId = getMarketplaceId();
                const sfId = getAdvertiserId();
                if (!marketplaceId || !sfId) {
                    notificationStore
                        .getState()
                        .upsertNotification(
                            "Missing either marketplace id or advertiser id",
                            NotificationType.ERROR,
                        );
                    return;
                }
                const searchParams = new URLSearchParams();
                searchParams.set("marketplaceId", marketplaceId);
                searchParams.set("sfId", sfId);
                window.open(`/icm?${searchParams.toString()}`, "_blank");
            },
        },
    ],
    // Add more filter keys with their action buttons as needed
    // [UnifiedFilterKeys.BROWSE_NODE_NAME]: [...],
    // [UnifiedFilterKeys.OBJECTIVES]: [...],
    // etc.
};

/**
 * Helper enum to store the static names for the groups in the unified type dropdown
 */
export enum UnifiedTypeGroups {
    STANDALONE = "Standalone Insights",
    PACKAGED = "Packaged Insights",
    MARKETING = "Marketing Content",
    PACKAGED_UAT = "Packaged Insights UAT",
    PITCHED_CONTENT = "Pitched Content",
}

export interface UnifiedTypeGroupInfo {
    label: string;
    tooltip: string;
}
export const UnifiedTypeGroupInfo: Record<
    UnifiedTypeGroups,
    UnifiedTypeGroupInfo
> = {
    [UnifiedTypeGroups.STANDALONE]: {
        label: "Standalone Insights",
        tooltip:
            "Pre-computed advertiser-specific insights that are grouped at insight family (e.g. Audience Funnel)",
    },
    [UnifiedTypeGroups.PACKAGED]: {
        label: "Packaged Insights",
        tooltip:
            "Pre-analyzed, curated collections of data and information that are organized and presented in a readily consumable format for end users. These insights are often derived from large datasets or complex analyses and are distilled into key takeaways, trends, or actionable recommendations",
    },
    [UnifiedTypeGroups.MARKETING]: {
        label: "Marketing Content",
        tooltip:
            "Sales collateral (e.g. Product Marketing deck, Industry Marketing deck, One pagers etc.) that are used by sales users in their pitch decks",
    },
    [UnifiedTypeGroups.PACKAGED_UAT]: {
        label: "Packaged Insights UAT",
        tooltip:
            "Pre-analyzed, curated collections of data and information that are organized and presented in a readily consumable format for end users. These insights are often derived from large datasets or complex analyses and are distilled into key takeaways, trends, or actionable recommendations",
    },
    [UnifiedTypeGroups.PITCHED_CONTENT]: {
        label: "Pitched Content",
        tooltip:
            "Pitched content that has been uploaded directly by users from IHub, or previously from ACC",
    },
};

export const insightTypeLabelToValue = {
    [InsightType.INSIGHT]: UnifiedTypeGroups.STANDALONE,
    [InsightType.ISTORE]: UnifiedTypeGroups.STANDALONE,
    [InsightType.CAIRE]: UnifiedTypeGroups.PACKAGED,
    [InsightType.PACKAGING]: UnifiedTypeGroups.PACKAGED,
    [InsightType.CDB_TEMPLATE]: UnifiedTypeGroups.PACKAGED,
    [InsightType.MARKETING]: UnifiedTypeGroups.MARKETING,
    [InsightType.PITCHED_CONTENT]: UnifiedTypeGroups.PITCHED_CONTENT,
};
